import "./App.css";
import { useEffect, Suspense, type ComponentType } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
// import { ThemeProvider } from '@/context/ThemeContext' // Temporarily disabled to fix React errors
import { AuthProvider } from "@/context/AuthContext";
import { AdminStatusProvider } from "@/context/AdminStatusContext";
import { FamilyModeProvider } from "@/context/FamilyModeContext";
import { publicRoutes, protectedRoutes, inviteRoutes } from "@/routes";
import ProtectedLayout from "@/components/layout/ProtectedLayout";
import { ActivityTracker } from "@/components/system/ActivityTracker";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ProductionErrorBoundary } from "@/components/FallbackUI";
import { analytics } from "@/services/analytics";
import { SEO as Seo } from "@/components/seo/SEO";
import { RouteSEO } from "@/components/seo/RouteSEO";
import { ScrollToTop } from "@/components/routing/ScrollToTop";
import { validateEnvironment, logEnvironmentValidation } from "@/utils/env";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import { QueryProvider } from "@/providers/QueryProvider";

// Company portal (B2B2C): lazy-loaded for code splitting
const CompanyPortalLayout = lazyWithRetry(() => import("@/components/layout/CompanyPortalLayout"));
const CompanyPortalOverview = lazyWithRetry(
  () => import("@/pages/company-portal/CompanyPortalOverview")
);
const CompanyPortalEmployees = lazyWithRetry(
  () => import("@/pages/company-portal/CompanyPortalEmployees")
);
const CompanyPortalPending = lazyWithRetry(
  () => import("@/pages/company-portal/CompanyPortalPending")
);
const CompanyPortalAddEmployee = lazyWithRetry(
  () => import("@/pages/company-portal/CompanyPortalAddEmployee")
);
const CompanyPortalSettings = lazyWithRetry(
  () => import("@/pages/company-portal/CompanyPortalSettings")
);
const CompanyPortalInviteHr = lazyWithRetry(
  () => import("@/pages/company-portal/CompanyPortalInviteHr")
);
const CompanyPortalAcceptHrInvite = lazyWithRetry(
  () => import("@/pages/company-portal/CompanyPortalAcceptHrInvite")
);

const COMPANY_PORTAL_SKELETON_IDS = ["portal-stat-a", "portal-stat-b", "portal-stat-c"] as const;

/**
 * Company Portal Fallback.
 */
const CompanyPortalFallback = () => (
  <div
    className="container max-w-5xl mx-auto p-6 space-y-6"
    aria-busy="true"
    aria-label="Loading company portal"
  >
    <Skeleton className="h-10 w-64" />
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {COMPANY_PORTAL_SKELETON_IDS.map((id) => (
        <Skeleton key={id} className="h-28 rounded-xl" />
      ))}
    </div>
    <Skeleton className="h-72 w-full rounded-xl" />
  </div>
);

function CompanyPortalRoute({ Component }: Readonly<{ Component: ComponentType }>) {
  return (
    <Suspense fallback={<CompanyPortalFallback />}>
      <Component />
    </Suspense>
  );
}

/**
 * Initializes the application with system monitoring and routing setup.
 * @example
 * App()
 * Renders the main application component including routing and theme setup.
 * @param {void} - No parameters.
 * @returns {ReactElement} The application's main component with routing configured.
 * @description
 *   - System monitoring initialization occurs after a delay to prevent blocking the initial app render.
 *   - Utilizes `useEffect` hook for running the initial setup on component mount.
 *   - Includes both public and protected route setups using React Router.
 *   - Wraps the application in context providers like `QueryClientProvider`, `ThemeProvider`, and `AuthProvider`.
 */
function App() {
  useEffect(() => {
    // Initialize analytics only - disable system monitoring to prevent script errors
    setTimeout(() => {
      try {
        // Validate environment variables but don't block app rendering
        const envValidation = validateEnvironment();
        logEnvironmentValidation(envValidation);

        if (!envValidation.isValid) {
          console.warn(
            "⚠️ Environment validation failed, but app will continue:",
            envValidation.errors
          );
        }

        // Temporarily disable system monitoring as it causes script errors
        // initSystemMonitoring();
        analytics.init();
      } catch (err) {
        console.error("Failed to initialize analytics:", err);
        // Don't let analytics errors break the app
        try {
          analytics.handleError(err as Error, { context: "app_initialization" });
        } catch (analyticsError) {
          console.error("Analytics error handling failed:", analyticsError);
        }
      }
    }, 100);
  }, []);

  return (
    <ProductionErrorBoundary>
      <ErrorBoundary>
        <QueryProvider>
          {/* <ThemeProvider> */}
          <BrowserRouter>
            <AuthProvider>
              <AdminStatusProvider>
                <FamilyModeProvider>
                  <ScrollToTop />
                  <RouteSEO />
                  <ActivityTracker />
                  <div className="min-h-screen w-full max-w-none bg-white light">
                    <Routes>
                      {/* Public routes (excluding catch-all so /c/:companySlug can match first) */}
                      {publicRoutes
                        .filter((r) => r.path !== "*")
                        .map(({ path, element: Element }) => (
                          <Route key={path} path={path} element={<Element />} />
                        ))}

                      {/* Public invite acceptance routes */}
                      {inviteRoutes.map(({ path, element: Element }) => (
                        <Route key={path} path={path} element={<Element />} />
                      ))}

                      {/* Protected routes — single ProtectedLayout instance (Outlet) prevents auth remount flash */}
                      <Route element={<ProtectedLayout />}>
                        {protectedRoutes.map(({ path, element: Element }) => (
                          <Route key={path} path={path} element={<Element />} />
                        ))}

                        <Route
                          path="/c/:companySlug"
                          element={
                            <Suspense fallback={<CompanyPortalFallback />}>
                              <CompanyPortalLayout />
                            </Suspense>
                          }
                        >
                          <Route
                            index
                            element={<CompanyPortalRoute Component={CompanyPortalOverview} />}
                          />
                          <Route
                            path="employees"
                            element={<CompanyPortalRoute Component={CompanyPortalEmployees} />}
                          />
                          <Route
                            path="pending"
                            element={<CompanyPortalRoute Component={CompanyPortalPending} />}
                          />
                          <Route
                            path="add-employee"
                            element={<CompanyPortalRoute Component={CompanyPortalAddEmployee} />}
                          />
                          <Route
                            path="invite-hr"
                            element={<CompanyPortalRoute Component={CompanyPortalInviteHr} />}
                          />
                          <Route
                            path="accept-hr-invite"
                            element={<CompanyPortalRoute Component={CompanyPortalAcceptHrInvite} />}
                          />
                          <Route
                            path="settings"
                            element={<CompanyPortalRoute Component={CompanyPortalSettings} />}
                          />
                        </Route>
                      </Route>

                      {/* Catch-all (must be last so /c/:companySlug matches first) */}
                      {publicRoutes
                        .filter((r) => r.path === "*")
                        .map(({ path, element: Element }) => (
                          <Route key={path} path={path} element={<Element />} />
                        ))}
                    </Routes>
                    <Toaster />
                    {/* PWA Install Banner */}
                    <PWAInstallBanner />
                  </div>
                  {/* Global SEO Configuration */}
                  <Seo
                    title="TellerWand | Personal Finance Management SaaS"
                    description="TellerWand helps you track investments, expenses, and savings in one place. Manage mutual funds, stocks, PPF, EPF, and more with smart dashboards."
                    keywords="personal finance, wealth tracking, SaaS, investments, savings, TellerWand, PPF, EPF, mutual funds, retirement planning"
                    structuredData={[
                      { type: "organization" },
                      { type: "website" },
                      { type: "product" },
                    ]}
                  />
                </FamilyModeProvider>
              </AdminStatusProvider>
            </AuthProvider>
          </BrowserRouter>
          {/* </ThemeProvider> */}
        </QueryProvider>
      </ErrorBoundary>
    </ProductionErrorBoundary>
  );
}

export default App;
